import * as angular from 'angular';
import * as template from './template.html';
import './styling.scss';
import { CurrentUserService } from 'services/currentUser';

import * as _ from 'lodash';
import { formatAsDollarsWithoutZeroCents } from 'common/source/utilities/strings';
import { getWaterBackupCoverageDisplay } from 'common/source/utilities/policyInfoDisplay';
import { hasActiveWindCoverage, isDp3Policy, isWindCoverageExcluded } from 'common/source/utilities/policyInfoHelper';

const module = angular.module('coverage-enhanced-coverage-section', []);

interface CoverageEnhancedCoverageSectionController extends angular.IStrictController {
    title: string;
    icon: string;
    description: string;
    coverages: Array<NameAndValueAndDescription>;
}

const ENHANCED_COVERAGE_DESCRIPTIONS = {
    EQUIPMENT_BREAKDOWN: (limit: string) => `Hippo Exclusive: Covers the cost of repair or replacement for pretty much
        everything in your home that uses electric power, in the event of electrical or mechanical breakdown. This
        includes your washer & dryer, HVAC equipment, kitchen appliances, water heaters, and even personal electronics like
        computers & TVs. This coverage protects up to ${limit} and the deductible is only $500, which is less than the
        standard deductible for other types of claims.`,
    SERVICE_LINE: (limit: string) => `Covers up to ${limit} (subject to a $500 deductible) for excavation, repair and 
        damaged property costs in the case of a problem with the service lines (electric, sewer, etc.) that runs from 
        your home to the street, whether caused by an accident or wear and tear.`,
    WORKERS_COMPENSATION: () => `Protects you if a household worker (such as a nanny, gardener or babysitter) is injured
        on your property.`,
    HOME_OFFICE: (limit: string) => `Covers home office equipment such as computers and printers up to ${limit}.`,
    COMPUTERS: (limit: string) => `Covers laptop & desktop computers and related storage devices up to ${limit}.`,
    WATER_BACKUP: (limit: string) => `Covers up to ${limit} for damages caused by backup and overflow of water
        from drains or sewers.`,
    LOSS_ASSESSMENT: () => `In the event of damage caused (by anyone) to your condo building or common areas, your HOA
        has limited insurance coverage. Any claim that exceeds this coverage, and any deductibles related to such claims
        (often as much as $50,000) are typically split among all Condo unit owners. Loss Assessment covers you for these
        costs, so you won’t be required to pay out of pocket.`,
    JEWELRY: (limit: string) => `Covers up to ${limit} for specific instances of loss or damage to jewelry and watches.
        For higher value items, Hippo also allows you to schedule specific items once your policy is purchased.`,
    MORTGAGE_PAYMENT_PROTECTION: () => `In the event that your home becomes uninhabitable, this coverage provides up to
        $1,500 a month, for up to 12 months, toward your mortgage payments (This coverage is included in Hippo’s Most
        Popular and Extra Coverage plans).`,
    WIND: () => `Additional coverage purchased to cover damage due to wind or hail, under a separate deductible.`
};

module.component('coverageEnhancedCoverageSection', {
    template: template,
    controller: function(
        this: CoverageEnhancedCoverageSectionController,
        currentUserService: CurrentUserService,
        $stateParams: PolicyStateParams
    ) {
        'ngInject';

        this.$onInit = () => {
            this.title = 'Enhanced Coverage';
            this.icon = 'ice-cream';
            this.coverages = [];

            const policyIndex = $stateParams.policyIndex;

            function getSpecialLimit(specialLimits: Array<SpecialLimit>, key: string) {
                return _.find(specialLimits, (item) => {
                    return item.key === key;
                });
            }

            const addCoverage = (condition: boolean, name: string, value: string, description: string) => {
                if (condition) {
                    this.coverages.push({
                        name: name,
                        value: value,
                        description: description
                    });
                }
            };

            const getHomeOfficeLimit = (product: string, businessPropertyIncreasedLimits: boolean) => {
                if (product === 'ho3') {
                    return businessPropertyIncreasedLimits ? '$10,000' : '$8,000';
                } else if (product === 'ho6') {
                    return '$5,000';
                } else {
                    return '';
                }
            };

            currentUserService.getCustomerPolicyInfoByIndex(policyIndex).then((policyInfo) => {
                const optionalCoverages = policyInfo.optional_coverages;
                const specialLimits = optionalCoverages.special_limits;
                const adjustments = policyInfo.adjustments;

                if (isDp3Policy(policyInfo)) {
                    this.description = `This insurance covers various items and systems like appliances, 
                                        service lines and more.`;
                } else {
                    this.description = `This insurance covers various items and systems like electronics,
                                        home office, appliances and more.`;
                }

                addCoverage(
                    !!(adjustments.equipment_breakdown && adjustments.equipment_breakdown.value),
                    'Equipment Breakdown',
                    '$100,000',
                    ENHANCED_COVERAGE_DESCRIPTIONS.EQUIPMENT_BREAKDOWN('$100,000')
                );

                addCoverage(
                    !!(adjustments.service_line && adjustments.service_line.value),
                    'Service Line',
                    '$10,000',
                    ENHANCED_COVERAGE_DESCRIPTIONS.SERVICE_LINE('$10,000')
                );

                addCoverage(
                    !!optionalCoverages.workers_compensation,
                    'Workers Comp',
                    'Included',
                    ENHANCED_COVERAGE_DESCRIPTIONS.WORKERS_COMPENSATION()
                );
                addCoverage(
                    optionalCoverages.workers_compensation === true,
                    'Domestic Workers',
                    'Included',
                    ENHANCED_COVERAGE_DESCRIPTIONS.WORKERS_COMPENSATION()
                );

                const homeOfficeLimit = getHomeOfficeLimit(
                    policyInfo.product,
                    optionalCoverages.business_property_increased_limits
                );

                addCoverage(
                    !!homeOfficeLimit,
                    'Home Office',
                    homeOfficeLimit,
                    ENHANCED_COVERAGE_DESCRIPTIONS.HOME_OFFICE(homeOfficeLimit)
                );

                const computers = getSpecialLimit(specialLimits, 'computers');

                if (computers) {
                    const computersLimit = formatAsDollarsWithoutZeroCents(computers.value);
                    addCoverage(
                        true,
                        'Computers',
                        computersLimit,
                        ENHANCED_COVERAGE_DESCRIPTIONS.COMPUTERS(computersLimit)
                    );
                }
                const waterBackupCoverageDisplay = getWaterBackupCoverageDisplay(policyInfo);

                if (waterBackupCoverageDisplay) {
                    addCoverage(
                        true,
                        'Water Backup, Sump Discharge or Overflow',
                        waterBackupCoverageDisplay,
                        ENHANCED_COVERAGE_DESCRIPTIONS.WATER_BACKUP(waterBackupCoverageDisplay)
                    );
                }

                if (!(policyInfo.product === 'ho3' && !policyInfo.property_data.hoa_membership)) {
                    addCoverage(
                        !!adjustments.loss_assessment,
                        'Loss Assessment',
                        formatAsDollarsWithoutZeroCents(adjustments.loss_assessment.value),
                        ENHANCED_COVERAGE_DESCRIPTIONS.LOSS_ASSESSMENT()
                    );
                }

                const jewelry = getSpecialLimit(specialLimits, 'jewelry');

                if (jewelry) {
                    const jewelryLimit = formatAsDollarsWithoutZeroCents(jewelry.value);
                    addCoverage(true, 'Jewelry', jewelryLimit, ENHANCED_COVERAGE_DESCRIPTIONS.JEWELRY(jewelryLimit));
                }

                addCoverage(
                    !!(adjustments.mortgage_payment_protection && adjustments.mortgage_payment_protection.value),
                    'Mortgage Payment Protection',
                    'Included',
                    ENHANCED_COVERAGE_DESCRIPTIONS.MORTGAGE_PAYMENT_PROTECTION()
                );

                addCoverage(
                    isWindCoverageExcluded(policyInfo),
                    'Wind Coverage',
                    hasActiveWindCoverage(policyInfo) ? 'Included' : 'Not Included',
                    ENHANCED_COVERAGE_DESCRIPTIONS.WIND()
                );
            });
        };
    }
});

export { module };
